import React from "react";
import { motion } from "framer-motion";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

// Paleta del tablero. Es la de la marca: navy + turquesa, con los semaforos encima.
const C = {
  rojo: "#dc2626", amarillo: "#d97706", verde: "#16a34a",
  navy: "#191d3f", teal: "#13a3ac", gris: "#94a3b8",
};
const SEV = {
  critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#16a34a", info: "#64748b",
};
const SEV_ES = {
  critical: "Crítica", high: "Alta", medium: "Media", low: "Baja", info: "Informativa",
};

const VEREDICTO = {
  rojo: { titulo: "Requiere atención ahora", sub: "Hay algo que no puede esperar" },
  amarillo: { titulo: "Atención", sub: "Hay cosas para revisar, nada urgente" },
  verde: { titulo: "Todo en orden", sub: "Sin señales que requieran acción inmediata" },
};

const Semaforo = ({ estado }) => (
  <div className="flex flex-col gap-1.5" aria-hidden="true">
    {["rojo", "amarillo", "verde"].map((c) => (
      <span key={c} className="w-3.5 h-3.5 rounded-full transition-all duration-500"
        style={{
          backgroundColor: estado === c ? C[c] : "rgba(255,255,255,.14)",
          boxShadow: estado === c ? `0 0 14px ${C[c]}` : "none",
        }} />
    ))}
  </div>
);

const EstadoGeneral = ({ estado, luces }) => {
  const v = VEREDICTO[estado] || VEREDICTO.verde;
  const encendidas = luces.filter((l) => l.estado !== "verde");
  return (
    <div data-testid="tablero-estado-general"
      className="rounded-2xl p-6 flex items-center gap-6 shadow-sm"
      style={{ background: C.navy }}>
      <Semaforo estado={estado} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-[.14em] font-semibold" style={{ color: C.teal }}>
          Estado general
        </p>
        <h2 className="text-2xl font-bold text-white mt-1" data-testid="tablero-veredicto">{v.titulo}</h2>
        <p className="text-sm mt-0.5" style={{ color: "#afb2bb" }}>
          {encendidas.length
            ? encendidas.map((l) => l.nombre.toLowerCase()).join(" · ")
            : v.sub}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-4xl font-bold text-white leading-none" data-testid="tablero-luces-encendidas">
          {encendidas.length}
        </p>
        <p className="text-[11px] mt-1" style={{ color: "#afb2bb" }}>
          de {luces.length} señales
        </p>
      </div>
    </div>
  );
};

const Luz = ({ luz, onClick }) => (
  <button data-testid={"tablero-luz-" + luz.id} onClick={onClick}
    className="text-left bg-white rounded-xl border p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
    style={{ borderColor: luz.estado === "verde" ? "#e8edf5" : C[luz.estado] + "55" }}>
    <div className="flex items-start justify-between gap-2">
      <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0"
        style={{
          backgroundColor: C[luz.estado],
          boxShadow: luz.estado !== "verde" ? `0 0 9px ${C[luz.estado]}` : "none",
        }} />
      <span className="text-2xl font-bold leading-none"
        style={{ color: luz.estado === "verde" ? C.gris : C[luz.estado] }}>
        {luz.valor}
      </span>
    </div>
    <p className="text-sm font-semibold text-gray-800 mt-2.5 leading-tight">{luz.nombre}</p>
    <p className="text-xs text-gray-500 mt-1 leading-snug">{luz.detalle}</p>
    <p className="text-[10px] uppercase tracking-wider mt-2 font-semibold"
      style={{ color: luz.via === "agente" ? C.teal : C.navy }}>
      {luz.via === "agente" ? "Agente" : "Perimetral"}
    </p>
  </button>
);

const BarrasSeveridad = ({ severidades, testid }) => {
  const data = ["critical", "high", "medium", "low", "info"]
    .map((k) => ({ nombre: SEV_ES[k], valor: severidades?.[k] || 0, color: SEV[k] }))
    .filter((d) => d.valor > 0);
  if (!data.length) {
    return <p className="text-sm text-gray-400 py-8 text-center">Sin registros</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={150}>
      <BarChart data={data} layout="vertical" margin={{ left: 4, right: 18, top: 4, bottom: 0 }}
        data-testid={testid}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="nombre" width={66} axisLine={false} tickLine={false}
          tick={{ fontSize: 11, fill: "#64748b" }} />
        <Tooltip cursor={{ fill: "#f1f5f9" }}
          contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e8edf5" }}
          formatter={(v) => [v, "Cantidad"]} />
        <Bar dataKey="valor" radius={[0, 5, 5, 0]} barSize={16}>
          {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const Dato = ({ label, valor, color }) => (
  <div className="flex items-baseline justify-between py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-semibold" style={{ color: color || "#0f172a" }}>{valor}</span>
  </div>
);

const Via = ({ titulo, bajada, acento, severidades, testid, children }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-start gap-2.5 mb-1">
      <span className="w-1 h-9 rounded-full shrink-0" style={{ background: acento }} />
      <div>
        <h3 className="font-semibold text-gray-800 leading-tight">{titulo}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{bajada}</p>
      </div>
    </div>
    <BarrasSeveridad severidades={severidades} testid={testid + "-barras"} />
    <div className="mt-2">{children}</div>
  </div>
);

const Tendencia = ({ datos, marca }) => {
  const data = (datos || []).map((d) => ({ ...d, dia: d.fecha.slice(8) + "/" + d.fecha.slice(5, 7) }));
  return (
    <div data-testid="tablero-tendencia" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <h3 className="font-semibold text-gray-800">Últimos 14 días</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">
        Cuántas señales llegaron por día desde cada vía
      </p>
      <ResponsiveContainer width="100%" height={190}>
        <AreaChart data={data} margin={{ left: -22, right: 8, top: 6, bottom: 0 }}>
          <defs>
            <linearGradient id="gPerim" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.navy} stopOpacity={0.5} />
              <stop offset="100%" stopColor={C.navy} stopOpacity={0.04} />
            </linearGradient>
            <linearGradient id="gAgente" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.teal} stopOpacity={0.5} />
              <stop offset="100%" stopColor={C.teal} stopOpacity={0.04} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="dia" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e8edf5" }} />
          <Area type="monotone" dataKey="seguria" name={marca} stroke={C.navy} strokeWidth={2} fill="url(#gPerim)" />
          <Area type="monotone" dataKey="agente" name="Agente" stroke={C.teal} strokeWidth={2} fill="url(#gAgente)" />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex gap-4 justify-center mt-1">
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: C.navy }} /> {marca} (perimetral)
        </span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: C.teal }} /> Agente (interno)
        </span>
      </div>
    </div>
  );
};

const QueHacer = ({ acciones }) => (
  <div data-testid="tablero-que-hacer" className="bg-white rounded-2xl border border-gray-100 shadow-sm">
    <div className="px-5 py-4 border-b border-gray-100">
      <h3 className="font-semibold text-gray-800">Qué hacer ahora</h3>
      <p className="text-xs text-gray-500 mt-0.5">Ordenado por urgencia, no por fecha</p>
    </div>
    {!acciones?.length ? (
      <p className="text-center text-sm text-gray-400 py-10">
        No hay nada pendiente que requiera acción inmediata
      </p>
    ) : (
      <ol className="divide-y divide-gray-50">
        {acciones.map((a, i) => (
          <li key={i} data-testid={"tablero-accion-" + i} className="px-5 py-3.5 flex gap-3.5 items-start">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0 mt-0.5"
              style={{ background: a.prioridad === 1 ? C.rojo : a.prioridad === 2 ? "#ea580c" : C.amarillo }}>
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 leading-snug">{a.titulo}</p>
              <p className="text-xs text-gray-500 mt-0.5">{a.detalle}</p>
              <p className="text-xs mt-1" style={{ color: C.teal }}>{a.que_hacer}</p>
            </div>
            <span className="text-[10px] uppercase tracking-wider font-semibold shrink-0 mt-1"
              style={{ color: a.via === "agente" ? C.teal : C.navy }}>
              {a.via === "agente" ? "Agente" : "Perimetral"}
            </span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export const SiemTablero = ({ tablero, marca, wazuhConectado, monitoresResueltos, onVerAlertas, onIrAgentes }) => {
  if (!tablero) return null;
  const { estado, luces = [], externo = {}, agente = {}, tendencia = [], acciones = [] } = tablero;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
      <EstadoGeneral estado={estado} luces={luces} />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="tablero-luces">
        {luces.map((l) => (
          <Luz key={l.id} luz={l}
            onClick={() => (l.id === "agentes_caidos" ? onIrAgentes() : onVerAlertas(l))} />
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Via testid="tablero-via-perimetral" acento={C.navy}
          titulo={"Desde afuera · " + marca}
          bajada="Lo que ven los escáneres y los monitores, sin tocar la máquina"
          severidades={externo.severidades}>
          <Dato label="Monitores activos" valor={externo.monitores_activos ?? 0} />
          <Dato label="Con hallazgos que persisten" valor={externo.persisten ?? 0}
            color={externo.persisten ? C.amarillo : undefined} />
          <Dato label="Reaparecieron" valor={externo.regresiones ?? 0}
            color={externo.regresiones ? C.rojo : undefined} />
          <Dato label="Remediados esta semana" valor={externo.remediados_semana ?? 0}
            color={externo.remediados_semana ? C.verde : undefined} />
          <Dato label="Monitores resueltos" valor={monitoresResueltos ?? 0} />
        </Via>

        <Via testid="tablero-via-agente" acento={C.teal}
          titulo="Desde adentro · Agente"
          bajada="Lo que reporta el agente instalado en el equipo del cliente. La auditoría de rutina se cuenta aparte."
          severidades={agente.severidades}>
          <Dato label="Agentes reportando" valor={(agente.activos ?? 0) + " de " + (agente.total ?? 0)}
            color={agente.caidos ? C.rojo : agente.total ? C.verde : undefined} />
          <Dato label="Eventos de seguridad" valor={agente.total_eventos ?? 0} />
          {agente.controles_configuracion > 0 && (
            <Dato label="Controles de configuración (CIS)" valor={agente.controles_configuracion}
              color={C.gris} />
          )}
          {agente.cambios_archivos > 0 && (
            <Dato label="Cambios en archivos (inventario)" valor={agente.cambios_archivos}
              color={C.gris} />
          )}
          <Dato label="Conexión con Wazuh" valor={wazuhConectado ? "Online" : "Offline"}
            color={wazuhConectado ? C.verde : C.rojo} />
          {(agente.detalle || []).map((d) => (
            <Dato key={d.id} label={d.nombre} valor={d.activo ? "Activo" : d.status}
              color={d.activo ? C.verde : C.rojo} />
          ))}
        </Via>
      </div>

      <Tendencia datos={tendencia} marca={marca} />
      <QueHacer acciones={acciones} />
    </motion.div>
  );
};

export default SiemTablero;
